<template>
    <div class="cf-container">
        <md-table v-if="hasOverrideDayWindows">
            <md-table-row slot="header">
                <md-table-head class="time-range-columns">From</md-table-head>
                <md-table-head class="time-range-columns">To</md-table-head>
                <md-table-head class="max-stops-column">Max stops</md-table-head>
                <md-table-head v-if="hasCapacitySettings('volume')" class="capacity-settings-columns">
                    Max volume
                </md-table-head>
                <md-table-head v-if="hasCapacitySettings('weight')" class="capacity-settings-columns">
                    Max weight
                </md-table-head>
                <md-table-head v-if="hasCapacitySettings('quantity')" class="capacity-settings-columns">
                    Max quantity
                </md-table-head>
                <md-table-head class="cut-off-label-column">Cut off</md-table-head>
                <md-table-head>Actions</md-table-head>
            </md-table-row>
            <md-table-row v-for="(overrideDayWindow) in filteredOverrideDayWindows" :key="overrideDayWindow.overrideDayWindowId">
                <md-table-cell class="time-range-columns">
                    <form-group>
                        <md-icon>access_time</md-icon>
                        <time-picker
                            :time="overrideDayWindow.startTime"
                            :all-time-options="false"
                            :is-required="true"
                            @selectedTime="
                                (selectedTime, isTimeValid) => {
                                    handleSetStartTime(selectedTime, isTimeValid, overrideDayWindow);
                                }
                            "
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell class="time-range-columns">
                    <form-group>
                        <md-icon>access_time</md-icon>
                        <time-picker
                            :time="overrideDayWindow.endTime"
                            :all-time-options="false"
                            :is-required="true"
                            @selectedTime="
                                (selectedTime, isTimeValid) => {
                                    handleSetEndTime(selectedTime, isTimeValid, overrideDayWindow);
                                }
                            "
                        />
                    </form-group>
                </md-table-cell>
                <md-table-cell class="max-stops-column">
                    <form-group>
                        <md-input v-model.number="overrideDayWindow.maxStops" type="number" />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('volume')" class="capacity-settings-columns">
                    <form-group>
                        <md-input v-model.number="overrideDayWindow.maxVolume" type="number" />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('weight')" class="capacity-settings-columns">
                    <form-group>
                        <md-input v-model.number="overrideDayWindow.maxWeight" type="number" />
                    </form-group>
                </md-table-cell>
                <md-table-cell v-if="hasCapacitySettings('quantity')" class="capacity-settings-columns">
                    <form-group>
                        <md-input v-model.number="overrideDayWindow.maxQuantity" type="number" />
                    </form-group>
                </md-table-cell>
                <md-table-cell
                    :class="
                        `cut-off-label-column ${
                            $_isValidServiceAreaCutOffCombination(overrideDayWindow, dayOfTheWeek)
                                ? ''
                                : 'invalid-cutt-off-options'
                        }`
                    "
                >
                    <md-icon>access_time</md-icon>
                    {{ $_generateServiceAreaCutOffLabel(overrideDayWindow, dayOfTheWeek) }}
                    <div class="edit-cutoff-button" @click="() => handleEditCuttOff(overrideDayWindow, dayOfTheWeek)">
                        <md-icon>arrow_drop_down</md-icon>
                    </div>
                </md-table-cell>
                <md-table-cell>
                    <md-button
                        title="Delete override day window"
                        class="md-danger md-just-icon md-round"
                        @click="deleteOverrideDayWindow(overrideDayWindow)"
                    >
                        <md-icon>delete</md-icon>
                    </md-button>
                </md-table-cell>
            </md-table-row>
        </md-table>
        <div class="text-center" v-if="!hasOverrideDayWindows">
            <p class="no-result-message">List is empty.</p>
        </div>
        <div class="add-override-day-window-container">
            <md-button class="md-button md-primary add-override-day-window-button" @click="handleAddOverrideDayWindow">
                <md-icon>add</md-icon>
                Add Delivery Window
            </md-button>
        </div>
    </div>
</template>

<script>
import { TimePicker } from '@/components';
import { mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { rowStatus } from '@/helpers';
import ServiceAreasCutOffModal from '../ServiceAreasCutOffModal';

export default {
    name: 'ServiceAreasOverrideDayWindow',
    components: {
        TimePicker
    },
    mixins: [GeneralMixin],
    props: {
        serviceAreaId: {
            type: String,
            default: null
        },
        overrideDayWindowData: {
            type: Object,
            default: () => ({ overrideDayWindows: [] })
        }
    },
    data() {
        return {
            dayOfTheWeek: null
        };
    },
    async mounted() {
        this.dayOfTheWeek = this.getDayNumber(this.overrideDayWindowData.overrideDate);
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        filteredOverrideDayWindows() {
            return (this.overrideDayWindowData?.overrideDayWindows || []).filter(
                (x) => x.rowStatus !== rowStatus.DELETE
            );
        },
        hasOverrideDayWindows() {
            return this.overrideDayWindowData?.overrideDayWindows?.length > 0;
        }
    },
    methods: {
        deleteOverrideDayWindow(overrideDayWindow) {
            const index = this.overrideDayWindowData.overrideDayWindows.indexOf(overrideDayWindow);

            if (index !== -1) {
                overrideDayWindow.rowStatus = rowStatus.DELETE;
                return;
            }

            if (overrideDayWindow.rowStatus === rowStatus.ADD && index !== -1) {
                this.overrideDayWindowData.overrideDayWindows.splice(index, 1);
            }
        },
        hasCapacitySettings(...capacityTypes) {
            const { vehicleCapacityUnitsConfiguration } = this.user;
            return (
                Array.isArray(vehicleCapacityUnitsConfiguration) &&
                vehicleCapacityUnitsConfiguration.some((x) => capacityTypes.some((y) => x.type === y))
            );
        },
        getDayNumber(dateString) {
            return new Date(dateString).getDay();
        },
        handleSetStartTime(selectedTime, isTimeValid, overrideDayWindow) {
            if (isTimeValid) {
                overrideDayWindow.startTime = selectedTime.appointmentTime;
                if (![rowStatus.DELETE, rowStatus.ADD].includes(overrideDayWindow.rowStatus)) {
                    overrideDayWindow.rowStatus = rowStatus.EDIT;
                }
            }
        },
        handleSetEndTime(selectedTime, isTimeValid, overrideDayWindow) {
            if (isTimeValid) {
                overrideDayWindow.endTime = selectedTime.appointmentTime;
                if (![rowStatus.DELETE, rowStatus.ADD].includes(overrideDayWindow.rowStatus)) {
                    overrideDayWindow.rowStatus = rowStatus.EDIT;
                }
            }
        },
        handleAddOverrideDayWindow() {
            if (!Array.isArray(this.overrideDayWindowData.overrideDayWindows)) {
                this.overrideDayWindowData.overrideDayWindows = [];
            }

            const newOverrideDayWindow = {
                overrideDayWindowId: null,
                startTime: null,
                endTime: null,
                maxStops: null,
                maxVolume: null,
                maxWeight: null,
                maxQuantity: null,
                cutOffType: null,
                cutOffTimeBeforeSlotValue: null,
                cutOffTimeBeforeSlotType: null,
                cutOffByTimeValue: null,
                cutOffByTimeType: null,
                cutOffByTimeNDaysPriorValue: null,
                rowStatus: rowStatus.ADD
            };

            this.overrideDayWindowData.overrideDayWindows.push(newOverrideDayWindow);
        },
        handleEditCuttOff(overrideDayWindow, dayOfTheWeek) {
            this.$modal
                .show(ServiceAreasCutOffModal, {
                    deliveryWindow: overrideDayWindow,
                    currentDayOfTheWeek: dayOfTheWeek
                })
                .then((response) => {
                    Object.assign(overrideDayWindow, response);
                    this.$modal.hide();
                });
        }
    }
};
</script>
<style lang="scss" scoped>
.add-override-day-window-container {
    border-top: 1px solid #dddddd;
}

.add-override-day-window-button,
.add-override-day-window-button:hover {
    margin-left: 0;
    background-color: #5f6771 !important;
    margin-top: 10px;
}

.edit-cutoff-button {
    display: inline-block;
    cursor: pointer;
}

::v-deep .vs--searchable .vs__dropdown-toggle {
    border-bottom: 0 !important;
}

.invalid-cutt-off-options {
    color: #ff1744;
}

.max-stops-column,
.capacity-settings-columns {
    width: 115px;
    min-width: 115px;
    max-width: 115px;
    input {
        width: 95px;
    }
}

.time-range-columns {
    width: 175px;
    min-width: 175px;
    max-width: 175px;
}

.cut-off-label-column {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
}
</style>
